import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Save, Play, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

function badge(source) {
  if (source === 'db') return { text: 'Configured', cls: 'bg-emerald-500/15 text-emerald-400' };
  if (source === 'env') return { text: 'Configured (env)', cls: 'bg-emerald-500/15 text-emerald-400' };
  return { text: 'Not set', cls: 'bg-slate-500/15 text-slate-400' };
}

function ServiceCard({ svcKey, svc, onSaved }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [testState, setTestState] = useState(null); // {ok, message}
  const conf = badge(Object.values(svc.fields).find(f => !f.optional)?.source || 'none');

  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/integrations/${svcKey}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(vals),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      setVals({});
      toast.success(`${svc.label} saved`);
      onSaved && onSaved();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setTestState(null);
    try {
      const r = await fetch(`${API}/integrations/${svcKey}/test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      setTestState(d);
      d.ok ? toast.success(d.message) : toast.error(d.message);
    } catch (e) { setTestState({ ok: false, message: String(e.message || e) }); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid={`integration-card-${svcKey}`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-white">{svc.label}</h3>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${conf.cls}`}>{conf.text}</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{svc.description}</p>
          </div>
          {testState && (testState.ok
            ? <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            : <XCircle className="h-5 w-5 text-red-400" />)}
        </div>
        <div className="space-y-3">
          {Object.entries(svc.fields).map(([fk, f]) => (
            <div key={fk} className="space-y-1">
              <Label className="text-xs text-slate-300">{f.label}{f.source !== 'none' && <span className="text-[10px] text-slate-500 ml-1">(from {f.source})</span>}</Label>
              <Input
                type={f.secret ? 'password' : 'text'}
                value={vals[fk] ?? ''}
                placeholder={f.secret && f.configured ? `${f.hint} — leave blank to keep` : (f.placeholder || '')}
                onChange={e => setVals(v => ({ ...v, [fk]: e.target.value }))}
                className="bg-slate-900 border-slate-600 h-9 text-sm text-white"
                data-testid={`integration-${svcKey}-${fk}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Button size="sm" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-500 h-8"
            data-testid={`integration-save-${svcKey}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save
          </Button>
          <Button size="sm" variant="outline" onClick={test} disabled={busy}
            className="h-8 border-slate-600 text-slate-200" data-testid={`integration-test-${svcKey}`}>
            <Play className="h-3.5 w-3.5 mr-1" /> Test
          </Button>
          {testState && <span className={`text-xs ${testState.ok ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`integration-test-result-${svcKey}`}>{testState.message}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function IntegrationsPanel() {
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/integrations`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load integrations');
      setServices((await r.json()).services);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center gap-2 text-slate-400 p-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (!services) return <div className="text-slate-400 p-8">No integrations available.</div>;

  const order = ['twilio', 'tavily', 'llm'];
  return (
    <div className="space-y-4" data-testid="integrations-panel">
      <div>
        <h2 className="text-lg font-bold text-white flex items-center gap-2">Integrations</h2>
        <p className="text-xs text-slate-400 mt-1 max-w-3xl">Shared 3rd-party service keys (single source of truth). Blank fields keep the current value. If a service has no key here, it falls back to the deployment environment variable — so saving is safe.</p>
      </div>
      {order.filter(k => services[k]).map(k => (
        <ServiceCard key={k} svcKey={k} svc={services[k]} onSaved={load} />
      ))}
    </div>
  );
}
